import { Payout } from 'store/api/payout';
import { PaymentMethod } from 'store/api/payment-methods';
import { Paychecks, Adjustment, PayoutType, PaychecksTitle } from 'types';

type KeysPaycheck = keyof Omit<
  Paychecks,
  'event' | 'payoutItems' | 'refundAdjustments' | 'chargebackAdjustments'
>;

export const getTransformedPaychecks = (paychecks: Paychecks) => {
  const order: KeysPaycheck[] = [
    'grossSalesTotal',
    'netSalesTotal',
    'tipTotal',
    'commissionsTotal',
    'bonusesTotal',
    'reimbursementsTotal',
    'miscPositiveTotal',
    'totalPay',
    'chargebackTotal',
    'refundTotal',
    'advancesTotal',
    'freeShippingTotal',
    'miscNegativeTotal',
    'totalPayout',
  ];

  return order
    .filter((key) => {
      if (key === 'totalPay' || key === 'totalPayout') {
        return true;
      }
      if (
        key === 'chargebackTotal' &&
        buildDisplayChargebackAdjustments(paychecks.chargebackAdjustments)
          .length
      ) {
        return true;
      }
      if (key === 'refundTotal' && paychecks.refundAdjustments?.length) {
        return true;
      }
      return Math.round(Math.abs(Number(paychecks[key]) || 0) * 100) > 0;
    })
    .map((key) => ({
      key,
      value: paychecks[key],
      name: PaychecksTitle[key],
    }));
};

export interface DisplayChargebackAdjustment {
  key: string;
  label: string;
  amount: number;
  checkoutId?: number | string;
}

type ChargebackAdjustmentGroup = {
  key: string;
  positiveTotal: number;
  negativeTotal: number;
  sortValue: number | string;
  positiveRows: Adjustment[];
  negativeRows: Adjustment[];
  checkoutId?: number | string;
};

type SortableDisplayChargebackAdjustment = DisplayChargebackAdjustment & {
  sortValue: number | string;
};

const parseCheckoutIdFromLabel = (label?: string) => {
  return label?.match(/Chargeback #([^\s]+)/)?.[1];
};

const getChargebackGroupKey = (adjustment: Adjustment, index: number) => {
  const checkoutId =
    adjustment.checkoutId ?? parseCheckoutIdFromLabel(adjustment.label);

  return checkoutId ? `checkout-${checkoutId}` : `unmatched-${index}`;
};

const getChargebackSortValue = (adjustment: Adjustment) => {
  const checkoutId =
    adjustment.checkoutId ?? parseCheckoutIdFromLabel(adjustment.label);

  if (!checkoutId) {
    return adjustment.label ?? '';
  }

  const numericId = Number(checkoutId);

  return Number.isNaN(numericId) ? String(checkoutId) : numericId;
};

const getWonLabel = (group: ChargebackAdjustmentGroup) => {
  const wonRow =
    group.positiveRows.find((row) => row.label?.includes('Won')) ??
    group.positiveRows[0];

  if (wonRow?.label) {
    return wonRow.label;
  }

  return group.checkoutId
    ? `Chargeback #${group.checkoutId} - Won`
    : 'Chargeback - Won';
};

const getDeductionLabel = (group: ChargebackAdjustmentGroup) => {
  const lostRow = group.negativeRows.find((row) => row.label?.includes('Lost'));
  const newRow = group.negativeRows.find((row) => row.label?.includes('New'));
  const fallbackRow = group.negativeRows[0];

  if (lostRow?.label) {
    return lostRow.label;
  }
  if (newRow?.label) {
    return newRow.label;
  }
  if (fallbackRow?.label) {
    return fallbackRow.label;
  }

  return group.checkoutId
    ? `Chargeback #${group.checkoutId} - New`
    : 'Chargeback - New';
};

export const buildDisplayChargebackAdjustments = (
  adjustments: Adjustment[] = []
): DisplayChargebackAdjustment[] => {
  const groups = adjustments.reduce<Record<string, ChargebackAdjustmentGroup>>(
    (acc, adjustment, index) => {
      const amount = Number(adjustment.amount) || 0;
      const key = getChargebackGroupKey(adjustment, index);

      if (!acc[key]) {
        acc[key] = {
          key,
          positiveTotal: 0,
          negativeTotal: 0,
          positiveRows: [],
          negativeRows: [],
          sortValue: getChargebackSortValue(adjustment),
          checkoutId:
            adjustment.checkoutId ?? parseCheckoutIdFromLabel(adjustment.label),
        };
      }

      if (amount > 0) {
        acc[key].positiveTotal += amount;
        acc[key].positiveRows.push(adjustment);
      } else if (amount < 0) {
        acc[key].negativeTotal += amount;
        acc[key].negativeRows.push(adjustment);
      }

      return acc;
    },
    {}
  );

  return Object.values(groups)
    .reduce<SortableDisplayChargebackAdjustment[]>((acc, group) => {
      const amount = Number(
        (group.positiveTotal + group.negativeTotal).toFixed(2)
      );

      if (Math.abs(amount) < 0.005) {
        return acc;
      }

      acc.push({
        amount,
        key: group.key,
        sortValue: group.sortValue,
        checkoutId: group.checkoutId,
        label: amount > 0 ? getWonLabel(group) : getDeductionLabel(group),
      });

      return acc;
    }, [])
    .sort((a, b) => {
      if (Math.sign(a.amount) !== Math.sign(b.amount)) {
        return b.amount - a.amount;
      }

      if (typeof a.sortValue === 'number' && typeof b.sortValue === 'number') {
        return a.sortValue - b.sortValue;
      }

      return String(a.sortValue).localeCompare(String(b.sortValue));
    })
    .map((adjustment) => ({
      key: adjustment.key,
      label: adjustment.label,
      amount: adjustment.amount,
      checkoutId: adjustment.checkoutId,
    }));
};

export const minusTypes: KeysPaycheck[] = [
  'refundTotal',
  'advancesTotal',
  'chargebackTotal',
  'freeShippingTotal',
  'miscNegativeTotal',
];

export const boldTypes: KeysPaycheck[] = ['totalPay', 'totalPayout'];

export const getPayoutPaymentMethodOptions = (
  paymentMethods?: PaymentMethod[]
) =>
  paymentMethods
    ?.filter(({ id, name }) => {
      const label = name?.trim();

      return Boolean(id && label && !/^\d+$/.test(label));
    })
    .map(({ id, name }) => ({
      value: id,
      label: name.trim(),
    })) ?? [];

const signedAmountTypes = [
  PayoutType.Misc,
  PayoutType.Earnings,
  PayoutType.Adjustment,
  PayoutType.NegativeTip,
  PayoutType.NegativeEarnings,
  PayoutType.NegativeAdjustment,
];

const minusPayoutTypes = [
  PayoutType.Refund,
  PayoutType.Advance,
  PayoutType.NegativeTip,
  PayoutType.FreeShipping,
  PayoutType.PrepaidAdvance,
  PayoutType.NegativeEarnings,
  PayoutType.NegativeAdjustment,
];

export const getPayoutItemSignedAmount = (
  item: Pick<Payout, 'type' | 'amount'>
) => {
  const amount = Number(item.amount) || 0;

  if (signedAmountTypes.includes(item.type)) {
    return amount;
  }

  if (minusPayoutTypes.includes(item.type)) {
    return -Math.abs(amount);
  }

  return Math.abs(amount);
};

export const getSelectedPayoutTotal = (
  items: Payout[],
  selectedAmounts: Record<number, number>
) =>
  items.reduce((sum, item) => {
    const amount = selectedAmounts[item.id] || 0;
    const sign = getPayoutItemSignedAmount(item) < 0 ? -1 : 1;

    return sum + amount * sign;
  }, 0);

export const getFullPayoutItemAmount = (item: Payout) =>
  Math.abs(getPayoutItemSignedAmount(item));

const roundMoney = (value: number) => Number(value.toFixed(2));

export const getPayoutItemsForAmount = (
  items: Payout[] = [],
  payoutAmount: number,
  fullPayoutAmount: number
) => {
  const amount = roundMoney(Math.max(Number(payoutAmount) || 0, 0));
  const fullAmount = roundMoney(Math.max(Number(fullPayoutAmount) || 0, 0));

  if (amount <= 0) {
    return [];
  }

  if (amount >= fullAmount) {
    return items
      .map((item) => ({
        id: item.id,
        amount: getFullPayoutItemAmount(item),
      }))
      .filter(({ amount }) => amount > 0);
  }

  let remainingAmount = amount;

  return items.reduce<{ id: number; amount: number }[]>((result, item) => {
    if (remainingAmount <= 0) {
      return result;
    }

    const itemSignedAmount = getPayoutItemSignedAmount(item);

    if (itemSignedAmount <= 0) {
      return result;
    }

    const itemAmount = Math.min(itemSignedAmount, remainingAmount);
    remainingAmount = roundMoney(remainingAmount - itemAmount);

    return [...result, { id: item.id, amount: roundMoney(itemAmount) }];
  }, []);
};
